import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Form, 
  Input, 
  Select, 
  Message, 
  Header, 
  Icon, 
  Label, 
  Statistic,
  Grid,
  Segment,
  Tab
} from 'semantic-ui-react';
import { 
  PieChart, 
  Pie, 
  Cell, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';
import { API, showError } from '../../helpers';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const TokenUsage = () => {
  const [executionId, setExecutionId] = useState('');
  const [tokenUsages, setTokenUsages] = useState([]);
  const [tokenSummary, setTokenSummary] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);

  const loadExecutionTokenUsage = async () => {
    if (!executionId.trim()) {
      showError('请输入执行ID');
      return;
    }

    setLoading(true);
    try {
      // 获取详细使用记录
      const usageRes = await API.get(`/api/token/usage/execution/${executionId.trim()}`);
      if (usageRes.data.success) {
        setTokenUsages(usageRes.data.data || []);
      } else {
        showError(usageRes.data.message);
        setTokenUsages([]);
      }

      // 获取汇总统计
      const summaryRes = await API.get(`/api/token/usage/execution/${executionId.trim()}/summary`);
      if (summaryRes.data.success) {
        setTokenSummary(summaryRes.data.data);
      } else {
        showError(summaryRes.data.message);
        setTokenSummary(null);
      }
    } catch (error) {
      showError('获取Token使用数据失败');
      setTokenUsages([]);
      setTokenSummary(null);
    }
    setLoading(false);
  };

  const loadUserTokenStats = async () => {
    try {
      const res = await API.get(`/api/token/usage/user?days=${days}`);
      if (res.data.success) {
        setUserStats(res.data.data);
      } else {
        showError(res.data.message);
      }
    } catch (error) {
      showError('获取用户Token统计失败');
    }
  };

  useEffect(() => {
    loadUserTokenStats();
  }, [days]);

  const formatTokens = (tokens) => {
    return tokens ? tokens.toLocaleString() : '0';
  };

  const formatCost = (cost) => {
    return cost ? `$${cost.toFixed(4)}` : '$0.0000';
  };

  const renderExecutionSummary = () => {
    if (!tokenSummary) return null;

    return (
      <Grid columns={4} stackable>
        <Grid.Column>
          <Statistic color="blue">
            <Statistic.Value>{formatTokens(tokenSummary.total_tokens)}</Statistic.Value>
            <Statistic.Label>总Token数</Statistic.Label>
          </Statistic>
        </Grid.Column>
        <Grid.Column>
          <Statistic color="green">
            <Statistic.Value>{formatTokens(tokenSummary.total_prompt_tokens)}</Statistic.Value>
            <Statistic.Label>输入Token</Statistic.Label>
          </Statistic>
        </Grid.Column>
        <Grid.Column>
          <Statistic color="orange">
            <Statistic.Value>{formatTokens(tokenSummary.total_completion_tokens)}</Statistic.Value>
            <Statistic.Label>输出Token</Statistic.Label>
          </Statistic>
        </Grid.Column>
        <Grid.Column>
          <Statistic color="purple">
            <Statistic.Value>{formatCost(tokenSummary.total_cost)}</Statistic.Value>
            <Statistic.Label>总成本</Statistic.Label>
          </Statistic>
        </Grid.Column>
      </Grid>
    );
  };

  const renderTokenUsageTable = () => {
    if (tokenUsages.length === 0) {
      return (
        <Message>
          <Icon name="info circle" />
          暂无Token使用记录
        </Message>
      );
    }

    return (
      <Table celled striped>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>模型名称</Table.HeaderCell>
            <Table.HeaderCell>输入Token</Table.HeaderCell>
            <Table.HeaderCell>输出Token</Table.HeaderCell>
            <Table.HeaderCell>总Token</Table.HeaderCell>
            <Table.HeaderCell>成本</Table.HeaderCell>
            <Table.HeaderCell>请求时间</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {tokenUsages.map((usage, index) => (
            <Table.Row key={index}>
              <Table.Cell>
                <Label color="blue">{usage.model_name}</Label>
              </Table.Cell>
              <Table.Cell>{formatTokens(usage.prompt_tokens)}</Table.Cell>
              <Table.Cell>{formatTokens(usage.completion_tokens)}</Table.Cell>
              <Table.Cell><strong>{formatTokens(usage.total_tokens)}</strong></Table.Cell>
              <Table.Cell><strong>{formatCost(usage.cost)}</strong></Table.Cell>
              <Table.Cell>{new Date(usage.request_time).toLocaleString()}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    );
  };

  const renderUserStatsCharts = () => {
    if (!userStats) return null;

    const modelData = userStats.tokens_by_model || [];
    const dayData = userStats.tokens_by_day || [];

    return (
      <Grid columns={2} stackable>
        <Grid.Column>
          <Segment>
            <Header as="h4">按模型统计</Header>
            {modelData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={modelData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ model_name, percent }) => `${model_name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="total_tokens"
                  >
                    {modelData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [formatTokens(value), 'Token数']} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Message>暂无数据</Message>
            )}
          </Segment>
        </Grid.Column>
        <Grid.Column>
          <Segment>
            <Header as="h4">按日期统计</Header>
            {dayData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dayData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip formatter={(value) => [formatTokens(value), 'Token数']} />
                  <Legend />
                  <Line type="monotone" dataKey="total_tokens" stroke="#8884d8" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Message>暂无数据</Message>
            )}
          </Segment>
        </Grid.Column>
      </Grid>
    );
  };

  const renderUserStatsSummary = () => {
    if (!userStats) return null;

    return (
      <Grid columns={3} stackable>
        <Grid.Column>
          <Statistic color="blue">
            <Statistic.Value>{formatTokens(userStats.total_tokens)}</Statistic.Value>
            <Statistic.Label>总Token数</Statistic.Label>
          </Statistic>
        </Grid.Column>
        <Grid.Column>
          <Statistic color="green">
            <Statistic.Value>{formatCost(userStats.total_cost)}</Statistic.Value>
            <Statistic.Label>总成本</Statistic.Label>
          </Statistic>
        </Grid.Column>
        <Grid.Column>
          <Statistic color="orange">
            <Statistic.Value>{formatTokens(userStats.avg_tokens_per_request)}</Statistic.Value>
            <Statistic.Label>平均每次请求</Statistic.Label>
          </Statistic>
        </Grid.Column>
      </Grid>
    );
  };

  const panes = [
    {
      menuItem: '执行查询',
      render: () => (
        <Tab.Pane>
          <Segment>
            <Form>
              <Form.Group>
                <Form.Field width={12}>
                  <label>执行ID</label>
                  <Input
                    placeholder="输入执行ID查询Token使用情况..."
                    value={executionId}
                    onChange={(e) => setExecutionId(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && loadExecutionTokenUsage()}
                  />
                </Form.Field>
                <Form.Field width={4}>
                  <label>&nbsp;</label>
                  <Button primary onClick={loadExecutionTokenUsage} loading={loading}>
                    查询
                  </Button>
                </Form.Field>
              </Form.Group>
            </Form>
          </Segment>

          {tokenSummary && (
            <Segment>
              <Header as="h3">执行Token使用汇总</Header>
              {renderExecutionSummary()}
            </Segment>
          )}

          <Card fluid>
            <Card.Content>
              <Card.Header>Token使用详情</Card.Header>
              <Card.Meta>执行ID: {executionId}</Card.Meta>
            </Card.Content>
            <Card.Content>
              {loading ? (
                <Message>
                  <Icon name="circle notched" loading />
                  加载中...
                </Message>
              ) : (
                renderTokenUsageTable()
              )}
            </Card.Content>
          </Card>
        </Tab.Pane>
      )
    },
    {
      menuItem: '用户统计',
      render: () => (
        <Tab.Pane>
          <Segment>
            <Form>
              <Form.Group>
                <Form.Field>
                  <label>统计天数</label>
                  <Select
                    value={days}
                    onChange={(e, { value }) => setDays(value)}
                    options={[
                      { key: 7, text: '最近7天', value: 7 },
                      { key: 30, text: '最近30天', value: 30 },
                      { key: 90, text: '最近90天', value: 90 },
                      { key: 365, text: '最近365天', value: 365 }
                    ]}
                  />
                </Form.Field>
              </Form.Group>
            </Form>
          </Segment>

          {userStats && (
            <>
              <Segment>
                <Header as="h3">用户Token使用统计</Header>
                {renderUserStatsSummary()}
              </Segment>

              <Segment>
                <Header as="h3">详细分析</Header>
                {renderUserStatsCharts()}
              </Segment>
            </>
          )}
        </Tab.Pane>
      )
    }
  ];

  return (
    <div>
      <Header as="h2">
        <Icon name="chart line" />
        <Header.Content>
          Token使用管理
          <Header.Subheader>查看和分析Token使用情况</Header.Subheader>
        </Header.Content>
      </Header>

      <Tab panes={panes} />
    </div>
  );
};

export default TokenUsage;
